import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, SearchIcon, CloseIcon, TrashIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';
import Pagination from './Pagination';
import { downloadCsv } from './csvExport';
import { localDateStr } from './callUtils';

const DEFAULT_PAGE_SIZE = 50;
// Same silent-background-refresh pattern as BackendPayments.js.
const AUTO_REFRESH_MS = 10000;
// Mirror DAMAGED_PRODUCT_MAX_IMAGE_BYTES (server/ivr/routes.js) and
// DAMAGE_REPORT_MAX_IMAGES (server/ivr/db.js), so a bad pick fails at once.
const MAX_PHOTO_BYTES = 25 * 1024 * 1024;
const MAX_PHOTOS = 10;

// What is damaged. An agent can tick several; the free-text description
// covers anything these don't.
const DAMAGE_TYPES = [
  'Broken / cracked',
  'Torn / ripped',
  'Stained / dirty',
  'Bent / dented',
  'Scratched',
  'Water damaged',
  'Missing part',
  'Wrong item',
  'Packaging damaged',
  'Other',
];

const RESOLUTIONS = ['Replacement sent', 'Refund given', 'Part sent', 'Rejected (not valid)', 'Other'];

function formatDateTime(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatQty(qty) {
  const value = Number(qty);
  return Number.isFinite(value) ? value.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : qty;
}

const emptyProductRow = () => ({ name: '', qty: '1' });

// A picked photo, with a local preview URL until it's uploaded on submit.
function usePhotoPreviews(files) {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const next = files.map((file) => URL.createObjectURL(file));
    setUrls(next);
    return () => next.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);
  return urls;
}

// "Report Damage" form. Photos are uploaded first in one request, then the
// report is saved with their URLs (same two-step flow as Backend Payments).
function AddDamageReportModal({ onClose, onSubmit }) {
  const { authFetch } = useAuth();
  const [mobileNumber, setMobileNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [products, setProducts] = useState([emptyProductRow()]);
  const [damageTypes, setDamageTypes] = useState([]);
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]); // File[]
  const photoUrls = usePhotoPreviews(photos);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const addPhotos = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;
    const images = picked.filter((file) => file.type.startsWith('image/'));
    const tooBig = images.filter((file) => file.size > MAX_PHOTO_BYTES);
    const accepted = images.filter((file) => file.size <= MAX_PHOTO_BYTES);
    const room = MAX_PHOTOS - photos.length;
    const messages = [];
    if (images.length < picked.length) messages.push('Only photos can be attached.');
    if (tooBig.length) messages.push(`${tooBig.length} photo(s) over 25 MB were skipped.`);
    if (accepted.length > room) messages.push(`At most ${MAX_PHOTOS} photos — the extra ones were skipped.`);
    setError(messages.join(' '));
    if (room > 0 && accepted.length) setPhotos((prev) => [...prev, ...accepted.slice(0, room)]);
  };

  const toggleDamageType = (type) =>
    setDamageTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!mobileNumber.trim()) {
      setError('Mobile number is required.');
      return;
    }
    const filledProducts = products.filter((product) => product.name.trim());
    if (filledProducts.length === 0) {
      setError('Add the product that arrived damaged.');
      return;
    }
    if (filledProducts.some((product) => !(Number(product.qty) > 0))) {
      setError('Enter a quantity above 0 for each product.');
      return;
    }
    if (damageTypes.length === 0 && !description.trim()) {
      setError('Pick what is damaged, or describe it.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let imageUrls = [];
      if (photos.length) {
        const formData = new FormData();
        photos.forEach((photo) => formData.append('files', photo));
        const uploadResponse = await authFetch('/api/ivr/damaged-products/attachments', {
          method: 'POST',
          body: formData,
        });
        const uploadBody = await uploadResponse.json().catch(() => null);
        if (!uploadResponse.ok) throw new Error(uploadBody?.error || 'Photo upload failed');
        imageUrls = uploadBody?.urls || [];
      }
      await onSubmit({
        mobileNumber: mobileNumber.trim(),
        customerName: customerName.trim(),
        orderNumber: orderNumber.trim(),
        products: filledProducts.map((product) => ({ name: product.name.trim(), qty: Number(product.qty) })),
        damageType: damageTypes.join(', '),
        description: description.trim(),
        imageUrls,
      });
    } catch (err) {
      setError(err.message || 'Failed to save this report.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel modal-panel-narrow">
        <div className="modal-header">
          <div>
            <h2>Report Damaged Product</h2>
            <p className="modal-subtitle">Customer number, what arrived damaged, and photos of it.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Mobile Number</span>
            <input type="tel" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} autoFocus required />
          </label>
          <div className="damage-form-row">
            <label className="login-field">
              <span>Customer Name (optional)</span>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </label>
            <label className="login-field">
              <span>Order Number (optional)</span>
              <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
            </label>
          </div>

          <div className="login-field">
            <span>Damaged Product</span>
            {products.map((product, index) => (
              <div className="backend-payment-product-row" key={index}>
                <input
                  type="text"
                  value={product.name}
                  placeholder="Product name, e.g. Pressing Ham"
                  aria-label={`Product ${index + 1} name`}
                  onChange={(e) =>
                    setProducts((prev) => prev.map((row, i) => (i === index ? { ...row, name: e.target.value } : row)))
                  }
                />
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={product.qty}
                  placeholder="Qty"
                  aria-label={`Product ${index + 1} quantity`}
                  onChange={(e) =>
                    setProducts((prev) => prev.map((row, i) => (i === index ? { ...row, qty: e.target.value } : row)))
                  }
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  aria-label={`Remove product ${index + 1}`}
                  onClick={() =>
                    setProducts((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [emptyProductRow()]))
                  }
                >
                  <CloseIcon width={12} height={12} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-small backend-payment-add-product"
              onClick={() => setProducts((prev) => [...prev, emptyProductRow()])}
            >
              + Add product
            </button>
          </div>

          <div className="login-field">
            <span>What is damaged?</span>
            <div className="damage-type-chips" role="group" aria-label="Damage type">
              {DAMAGE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`damage-chip ${damageTypes.includes(type) ? 'active' : ''}`}
                  aria-pressed={damageTypes.includes(type)}
                  onClick={() => toggleDamageType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <label className="login-field">
            <span>Details</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Which part is damaged and how, e.g. the ham's stitching is torn on one side"
              rows={3}
            />
          </label>

          <div className="login-field">
            <span>
              Photos ({photos.length}/{MAX_PHOTOS})
            </span>
            <label
              className={`damage-dropzone ${dragging ? 'dragging' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                addPhotos(e.dataTransfer.files);
              }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  addPhotos(e.target.files);
                  e.target.value = ''; // allow re-picking the same file
                }}
              />
              <strong>Click to add photos</strong> or drop them here
            </label>
            {photos.length > 0 && (
              <div className="damage-photo-grid">
                {photos.map((photo, index) => (
                  <div className="damage-photo" key={`${photo.name}-${index}`}>
                    {photoUrls[index] && <img src={photoUrls[index]} alt={photo.name} />}
                    <button
                      type="button"
                      className="damage-photo-remove"
                      aria-label={`Remove ${photo.name}`}
                      onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <CloseIcon width={12} height={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (photos.length ? 'Uploading…' : 'Saving…') : 'Save Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Marks a report resolved, with how it was settled.
function ResolveModal({ report, onClose, onSubmit }) {
  const [choice, setChoice] = useState(RESOLUTIONS[0]);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const resolution = [choice === 'Other' ? '' : choice, note.trim()].filter(Boolean).join(' — ');
    if (!resolution) {
      setError('Say how it was resolved.');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(resolution);
    } catch (err) {
      setError(err.message || 'Failed to update this report.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel modal-panel-narrow">
        <div className="modal-header">
          <div>
            <h2>Mark Resolved</h2>
            <p className="modal-subtitle">{report.customer_name || report.mobile_number}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="login-field">
            <span>Resolution</span>
            <div className="damage-type-chips" role="radiogroup" aria-label="Resolution">
              {RESOLUTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={choice === option}
                  className={`damage-chip ${choice === option ? 'active' : ''}`}
                  onClick={() => setChoice(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <label className="login-field">
            <span>Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. new AWB 12345" />
          </label>
          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Mark Resolved'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Full-size photo viewer with previous/next (arrow keys too).
function PhotoViewer({ urls, index, onIndexChange, onClose }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onIndexChange((index - 1 + urls.length) % urls.length);
      if (event.key === 'ArrowRight') onIndexChange((index + 1) % urls.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, urls.length, onIndexChange, onClose]);

  return (
    <div className="modal-overlay modal-overlay-center damage-viewer" onClick={onClose}>
      <div className="damage-viewer-body" onClick={(e) => e.stopPropagation()}>
        <img src={urls[index]} alt={`Damaged product, ${index + 1} of ${urls.length}`} />
        <div className="damage-viewer-bar">
          {urls.length > 1 && (
            <button type="button" className="btn btn-secondary btn-small" aria-label="Previous photo"
              onClick={() => onIndexChange((index - 1 + urls.length) % urls.length)}>
              <ChevronLeftIcon width={14} height={14} />
            </button>
          )}
          <span>
            {index + 1} / {urls.length}
          </span>
          {urls.length > 1 && (
            <button type="button" className="btn btn-secondary btn-small" aria-label="Next photo"
              onClick={() => onIndexChange((index + 1) % urls.length)}>
              <ChevronRightIcon width={14} height={14} />
            </button>
          )}
          <a href={urls[index]} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-small">
            Open original
          </a>
          <button type="button" className="btn btn-secondary btn-small" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotosCell({ urls, onOpen }) {
  if (!urls?.length) return '-';
  const shown = urls.slice(0, 3);
  return (
    <div className="damage-photo-thumbs">
      {shown.map((url, index) => (
        <button key={url} type="button" className="damage-thumb-btn" onClick={() => onOpen(urls, index)} aria-label={`View photo ${index + 1}`}>
          <img src={url} alt="" className="international-order-bill-thumb" />
          {index === shown.length - 1 && urls.length > shown.length && (
            <span className="damage-thumb-more">+{urls.length - shown.length}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// Damaged Products — a log of products customers received damaged, kept in
// server/ivr/db.js's ivr_damaged_products. Open until an agent resolves it.
function DamagedProducts() {
  const { authFetch } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showAddForm, setShowAddForm] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [viewer, setViewer] = useState(null); // { urls, index }

  const fetchReports = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/damaged-products');
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        if (!silent) setReports([]);
        setError(body?.error || 'Failed to fetch damaged product reports.');
        return;
      }
      setError('');
      setReports(Array.isArray(body?.reports) ? body.reports : []);
    } catch (err) {
      if (!silent) setReports([]);
      setError(err.message || 'Failed to fetch damaged product reports.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    const interval = setInterval(() => fetchReports({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const counts = useMemo(
    () => ({
      all: reports.length,
      open: reports.filter((r) => r.status !== 'resolved').length,
      resolved: reports.filter((r) => r.status === 'resolved').length,
    }),
    [reports]
  );

  const filteredReports = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reports.filter((report) => {
      if (statusFilter === 'open' && report.status === 'resolved') return false;
      if (statusFilter === 'resolved' && report.status !== 'resolved') return false;
      if (!term) return true;
      return [
        report.mobile_number,
        report.customer_name,
        report.order_number,
        report.damage_type,
        report.description,
        report.resolution,
        ...(report.products || []).map((product) => product.name),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });
  }, [reports, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = filteredReports.slice((page - 1) * pageSize, page * pageSize);

  // Oldest report = 1, from the unfiltered list so it never reshuffles.
  const serialById = useMemo(() => {
    const map = new Map();
    [...reports]
      .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || a.id - b.id)
      .forEach((report, index) => map.set(report.id, index + 1));
    return map;
  }, [reports]);

  const replaceReport = (updated) => setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));

  const handleAdd = async (fields) => {
    const response = await authFetch('/api/ivr/damaged-products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to save this report.');
    setReports((prev) => [body, ...prev]);
    setShowAddForm(false);
  };

  const updateStatus = async (report, payload) => {
    const response = await authFetch(`/api/ivr/damaged-products/${report.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to update this report.');
    replaceReport(body);
  };

  const handleResolve = async (resolution) => {
    await updateStatus(resolving, { resolved: true, resolution });
    setResolving(null);
  };

  const handleReopen = async (report) => {
    setBusyId(report.id);
    try {
      await updateStatus(report, { resolved: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete the damage report for ${report.customer_name || report.mobile_number}?`)) return;
    setBusyId(report.id);
    try {
      const response = await authFetch(`/api/ivr/damaged-products/${report.id}`, { method: 'DELETE' });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Failed to delete this report.');
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (err) {
      setError(err.message || 'Failed to delete this report.');
    } finally {
      setBusyId(null);
    }
  };

  const handleExportCsv = () => {
    const headers = [
      'Sr No', 'Mobile Number', 'Customer Name', 'Order Number', 'Products', 'What Is Damaged', 'Details',
      'Photos', 'Status', 'Resolution', 'Resolved By', 'Resolved On', 'Logged By', 'Logged On',
    ];
    const rows = filteredReports.map((report) => [
      serialById.get(report.id),
      report.mobile_number || '',
      report.customer_name || '',
      report.order_number || '',
      (report.products || []).map((product) => `${product.name} x ${formatQty(product.qty)}`).join('; '),
      report.damage_type || '',
      report.description || '',
      (report.image_urls || []).join(' '),
      report.status === 'resolved' ? 'Resolved' : 'Open',
      report.resolution || '',
      report.resolved_by || '',
      report.resolved_at ? formatDateTime(report.resolved_at) : '',
      report.agent || '',
      report.created_at ? formatDateTime(report.created_at) : '',
    ]);
    downloadCsv(`damaged-products-${localDateStr(new Date())}.csv`, headers, rows);
  };

  return (
    <>
      <div className="stat-cards">
        <button type="button" className={`stat-card critical ${statusFilter === 'open' ? 'active' : ''}`} onClick={() => setStatusFilter('open')}>
          <div className="stat-value">{counts.open}</div>
          <div className="stat-label">Open</div>
        </button>
        <button type="button" className={`stat-card answered ${statusFilter === 'resolved' ? 'active' : ''}`} onClick={() => setStatusFilter('resolved')}>
          <div className="stat-value">{counts.resolved}</div>
          <div className="stat-label">Resolved</div>
        </button>
        <button type="button" className={`stat-card ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
          <div className="stat-value">{counts.all}</div>
          <div className="stat-label">All Reports</div>
        </button>
      </div>

      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchReports()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by number, name, order, product or damage"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search damaged product reports"
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary export-csv-btn"
          onClick={handleExportCsv}
          disabled={loading || filteredReports.length === 0}
        >
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setShowAddForm(true)}>
          + Report Damage
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Customer</th>
                <th>Order No.</th>
                <th>Damaged Product</th>
                <th>What Is Damaged</th>
                <th>Photos</th>
                <th>Status</th>
                <th>Logged</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load damaged product reports.'
                      : search
                      ? 'No reports match this search.'
                      : statusFilter === 'open'
                      ? 'No open damage reports.'
                      : 'No damage reports yet.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((report) => {
                  const resolved = report.status === 'resolved';
                  return (
                    <tr key={report.id}>
                      <td>{serialById.get(report.id)}</td>
                      <td>
                        {report.customer_name && <div className="damage-cell-strong">{report.customer_name}</div>}
                        <div>{report.mobile_number}</div>
                      </td>
                      <td>{report.order_number || '-'}</td>
                      <td>
                        {report.products?.length ? (
                          <ul className="backend-payment-products">
                            {report.products.map((product, index) => (
                              <li key={`${product.name}-${index}`}>
                                {product.name} <strong>× {formatQty(product.qty)}</strong>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="damage-cell-wrap">
                        {report.damage_type && (
                          <div className="damage-tags">
                            {report.damage_type.split(', ').map((type) => (
                              <span key={type} className="damage-tag">
                                {type}
                              </span>
                            ))}
                          </div>
                        )}
                        {report.description && <div className="damage-description">{report.description}</div>}
                      </td>
                      <td>
                        <PhotosCell urls={report.image_urls} onOpen={(urls, index) => setViewer({ urls, index })} />
                      </td>
                      <td className="damage-cell-wrap">
                        <span className={`status ${resolved ? 'answered' : 'missed'}`}>{resolved ? 'Resolved' : 'Open'}</span>
                        {resolved && report.resolution && <div className="damage-description">{report.resolution}</div>}
                        {resolved && (
                          <div className="damage-meta">
                            {report.resolved_by} · {formatDateTime(report.resolved_at)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>{formatDateTime(report.created_at)}</div>
                        <div className="damage-meta">{report.agent || '-'}</div>
                      </td>
                      <td>
                        <div className="damage-actions">
                          {resolved ? (
                            <button type="button" className="btn btn-secondary btn-small" disabled={busyId === report.id}
                              onClick={() => handleReopen(report)}>
                              Reopen
                            </button>
                          ) : (
                            <button type="button" className="btn btn-primary btn-small" disabled={busyId === report.id}
                              onClick={() => setResolving(report)}>
                              Resolve
                            </button>
                          )}
                          <button type="button" className="btn btn-danger-outline btn-small" disabled={busyId === report.id}
                            onClick={() => handleDelete(report)} aria-label="Delete report">
                            <TrashIcon width={14} height={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        totalItems={filteredReports.length}
      />

      {showAddForm && <AddDamageReportModal onClose={() => setShowAddForm(false)} onSubmit={handleAdd} />}
      {resolving && <ResolveModal report={resolving} onClose={() => setResolving(null)} onSubmit={handleResolve} />}
      {viewer && (
        <PhotoViewer
          urls={viewer.urls}
          index={viewer.index}
          onIndexChange={(index) => setViewer((prev) => ({ ...prev, index }))}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
}

export default DamagedProducts;
